import type { Rng, RngState } from './types.ts';

/** cyrb128 string hash → four 32-bit seeds. */
function hashSeed(seed: string): RngState {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < seed.length; i++) {
    const k = seed.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

const UINT32 = 0x1_0000_0000;

/**
 * sfc32 PRNG. Small, fast, passes PractRand to 2^40+; adequate for games.
 * Not cryptographically secure — shuffles are trusted to the host (ADR-0003).
 */
export function createRng(seed: string | RngState): Rng {
  let [a, b, c, d] = typeof seed === 'string' ? hashSeed(seed) : seed;

  const nextUint32 = (): number => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    const t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    const r = (t + d) | 0;
    c = (c + r) | 0;
    return r >>> 0;
  };

  // Discard the first outputs to decorrelate similar string seeds. A restored
  // snapshot must continue exactly where it left off, so it skips the warm-up.
  if (typeof seed === 'string') for (let i = 0; i < 12; i++) nextUint32();

  return {
    next: () => nextUint32() / UINT32,
    int: (maxExclusive: number) => {
      if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > UINT32) {
        throw new RangeError(`int(maxExclusive) requires an integer in [1, 2^32], got ${maxExclusive}`);
      }
      // Rejection sampling removes modulo bias.
      const limit = UINT32 - (UINT32 % maxExclusive);
      let x = nextUint32();
      while (x >= limit) x = nextUint32();
      return x % maxExclusive;
    },
    snapshot: () => [a >>> 0, b >>> 0, c >>> 0, d >>> 0] as const,
  };
}
